/* eslint-disable react-refresh/only-export-components */
import { Link, useMatchRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AppWindow,
  Bell,
  ChartLine,
  Check,
  ChevronsUpDown,
  CreditCard,
  Database,
  FolderSync,
  Gauge,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Megaphone,
  Plug,
  RefreshCw,
  Settings,
  Sparkles,
  User,
} from "lucide-react";
import {
  Avatar,
  Badge,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  NavigationMenu,
  Sidebar,
  UserMenu,
  type NavigationItem,
} from "@h2j-data-team/ui";

import { APP_VERSION } from "@/lib/version";
import { PRODUCT_NAME } from "@repo/shared";
import { organizationMembershipsQueryOptions } from "@/features/qlik-connections/api";
import { getStoredActiveOrganizationId, setActiveOrganizationId } from "@/lib/api-client";
import { queryClient } from "@/lib/query-client";
import { authClient } from "@/lib/auth-client";
import { fetchEndSessionUrl } from "@/lib/sso";
import { renderLink } from "@/components/router-link";
import { FeedbackDrawer } from "@/components/feedback-drawer";
import { alertsQueryOptions, useMarkAlertAsRead, type UserAlert } from "@/features/alerts/api";
import { useFormatDate } from "@/hooks/use-format-date";

/**
 * The caller's organizations and the active one. The active organization lives
 * in localStorage and travels in `x-organization-id` (lib/api-client); a stored
 * id the user no longer belongs to falls back to the first membership. Mount
 * once, in the authenticated layout.
 */
export function useActiveOrganization() {
  const { data: memberships = [], isLoading } = useQuery(organizationMembershipsQueryOptions);
  const [selectedId, setSelectedId] = useState<string | null>(() =>
    getStoredActiveOrganizationId(),
  );

  const activeId = useMemo(() => {
    if (memberships.length === 0) return null;
    return memberships.some((org) => org.id === selectedId)
      ? selectedId
      : (memberships[0]?.id ?? null);
  }, [selectedId, memberships]);

  useEffect(() => {
    if (!isLoading) setActiveOrganizationId(activeId);
  }, [activeId, isLoading]);

  // Switching resets the whole cache: every org-scoped screen refetches against
  // the new organization, and nothing of the previous one stays painted meanwhile
  // (invalidating would keep it on screen until the refetch lands).
  const switchTo = async (organizationId: string) => {
    if (organizationId === activeId) return;
    setSelectedId(organizationId);
    setActiveOrganizationId(organizationId);
    await queryClient.resetQueries();
  };

  const active = memberships.find((org) => org.id === activeId) ?? null;
  return { memberships, isLoading, active, switchTo };
}

type ActiveOrganization = ReturnType<typeof useActiveOrganization>;

/** Brand + organization switcher at the top of the dark sidebar. */
export function OrgSwitcher({ organization }: { organization: ActiveOrganization }) {
  const { t } = useTranslation("nav");
  const { memberships, isLoading, active, switchTo } = organization;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-sidebar-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white data-[state=open]:bg-sidebar-accent"
        >
          <span className="grid min-w-0 flex-1 gap-1 leading-tight">
            <img
              src="/senseguard-logo-negativo.svg"
              alt={PRODUCT_NAME}
              className="h-5 w-auto max-w-full"
            />
            <span className="truncate text-xs text-sidebar-muted">
              {isLoading
                ? t("loading", { ns: "common" })
                : active
                  ? `${active.name} · ${active.role}`
                  : t("noOrganization")}
            </span>
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-sidebar-muted" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-(--radix-popper-anchor-width) min-w-56"
        align="start"
        side="bottom"
      >
        <DropdownMenuLabel>{t("organizations")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {memberships.map((org) => (
          <DropdownMenuItem key={org.id} onSelect={() => void switchTo(org.id)}>
            <Avatar
              size="sm"
              shape="square"
              name={org.name}
              initials={org.name[0]?.toUpperCase()}
            />
            <span className="flex-1 truncate" title={org.name}>
              {org.name}
            </span>
            {org.id === active?.id && <Check className="ml-auto text-brand-text" aria-hidden />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Product navigation, grouped like the sidebar always was. Settings and plan usage are for admins and owners. */
export function AppNavigation({ organization }: { organization: ActiveOrganization }) {
  const { t } = useTranslation("nav");
  const matchRoute = useMatchRoute();
  const at = (to: string, fuzzy = true) => !!matchRoute({ to, fuzzy });
  const isAdminOrOwner =
    organization.active?.role === "owner" || organization.active?.role === "admin";

  const overview = t("overview");
  const qlik = t("qlikCloud");
  const account = t("account");

  const items: NavigationItem[] = [
    {
      id: "dashboard",
      group: overview,
      label: t("dashboard"),
      icon: <LayoutDashboard />,
      href: "/dashboard",
      active: at("/dashboard", false),
    },
    {
      id: "connections",
      group: qlik,
      label: t("connections"),
      icon: <Plug />,
      href: "/connections",
      active: at("/connections"),
    },
    {
      id: "single-app",
      group: qlik,
      label: t("singleApp"),
      icon: <AppWindow />,
      href: "/single-app",
      active: at("/single-app"),
    },
    {
      id: "sync-groups",
      group: qlik,
      label: t("syncGroups"),
      icon: <FolderSync />,
      href: "/sync-groups",
      active: at("/sync-groups"),
    },
    {
      id: "analyses",
      group: qlik,
      label: t("analyses"),
      icon: <ChartLine />,
      href: "/analyses",
      active: at("/analyses"),
    },
    {
      id: "qvd-analyses",
      group: qlik,
      label: t("qvdAnalyses"),
      icon: <Database />,
      href: "/qvd-analyses",
      active: at("/qvd-analyses"),
    },
    {
      id: "capacity",
      group: qlik,
      label: t("capacity"),
      icon: <Gauge />,
      href: "/capacity",
      active: at("/capacity"),
    },
    {
      id: "reloads",
      group: qlik,
      label: t("reloads"),
      icon: <RefreshCw />,
      href: "/reloads",
      active: at("/reloads"),
    },
    {
      id: "jobs",
      group: qlik,
      label: t("jobs"),
      icon: <ListChecks />,
      href: "/jobs",
      active: at("/jobs"),
    },
  ];
  if (isAdminOrOwner && organization.active) {
    items.push(
      {
        id: "settings",
        group: account,
        label: t("settings"),
        icon: <Settings />,
        href: "/settings",
        active: at("/settings"),
      },
      {
        id: "plan-usage",
        group: account,
        label: t("planUsage"),
        icon: <CreditCard />,
        href: "/organization/billing",
        active: at("/organization/billing"),
      },
    );
  }

  return (
    <Sidebar>
      <NavigationMenu
        label={t("shell.navigation", { ns: "common" })}
        items={items}
        renderLink={renderLink}
      />
    </Sidebar>
  );
}

/**
 * Account menu at the foot of the sidebar: name and email, notifications with
 * the unread count, profile, what's new with the version, feedback, language
 * and sign out. Signing out clears the whole query cache, so the next user never
 * sees this one's data.
 */
export function AccountMenu({ user }: { user: { name?: string | null; email: string } }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("nav");
  const { formatDate } = useFormatDate();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  // Feedback and notifications open from a menu item, without a Trigger: focus
  // goes back to the account button when they close.
  const accountTriggerRef = useRef<HTMLButtonElement>(null);
  const { data: alerts } = useQuery(alertsQueryOptions);
  const markAsRead = useMarkAlertAsRead();
  const unreadCount = alerts?.filter((a) => !a.isRead).length ?? 0;
  const language = i18n.language === "pt-BR" ? "pt-BR" : "en";

  const handleSignOut = async () => {
    // Read before the local sign-out: the URL belongs to the session being ended.
    const endSessionUrl = await fetchEndSessionUrl();
    setActiveOrganizationId(null);
    await authClient.signOut();
    queryClient.clear();
    if (endSessionUrl) {
      window.location.assign(endSessionUrl);
      return;
    }
    navigate({
      to: "/login",
      search: {
        mode: undefined,
        token: undefined,
        error: undefined,
        redirect: undefined,
        email: undefined,
      },
    });
  };

  return (
    <>
      <UserMenu
        ref={accountTriggerRef}
        label={t("shell.account", { ns: "common" })}
        name={user.name || user.email}
        detail={user.email}
        avatar={
          <span className="relative inline-flex">
            <Avatar size="md" name={user.name || user.email} />
            {unreadCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-status-danger" />
            )}
          </span>
        }
        className="min-w-0 flex-1"
      >
        <DropdownMenuItem onSelect={() => setNotificationsOpen(true)}>
          <Bell aria-hidden />
          {t("notifications")}
          {unreadCount > 0 && (
            <Badge variant="danger" className="ml-auto">
              {unreadCount}
            </Badge>
          )}
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/profile">
            <User aria-hidden />
            {t("profile")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/changelog">
            <Sparkles aria-hidden />
            <span>{t("whatsNew")}</span>
            <span className="ml-auto text-xs text-faint">v{APP_VERSION}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setFeedbackOpen(true)}>
          <Megaphone aria-hidden />
          {t("sendFeedback")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t("language")}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={language}
          onValueChange={(lng) => void i18n.changeLanguage(lng)}
        >
          <DropdownMenuRadioItem value="en">
            {t("language.en", { ns: "common" })}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="pt-BR">
            {t("language.ptBR", { ns: "common" })}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onSelect={() => void handleSignOut()}>
          <LogOut aria-hidden />
          {t("signOut")}
        </DropdownMenuItem>
      </UserMenu>

      <FeedbackDrawer
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        userEmail={user.email}
        userName={user.name}
        restoreFocusRef={accountTriggerRef}
      />
      <Dialog
        open={notificationsOpen}
        onOpenChange={setNotificationsOpen}
        restoreFocusRef={accountTriggerRef}
      >
        <DialogContent size="sm" closeLabel={t("close", { ns: "common" })}>
          <DialogHeader>
            <DialogTitle>{t("notifications")}</DialogTitle>
          </DialogHeader>
          <DialogBody className="max-h-[360px] overflow-y-auto">
            <NotificationsList
              alerts={alerts}
              onMarkRead={(id) => markAsRead.mutate(id)}
              formatDate={formatDate}
            />
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}

const ALERT_TYPE_BADGE: Record<
  string,
  { label: string; variant: "info" | "warning" | "default" | "secondary" }
> = {
  changelog: { label: "Changelog", variant: "info" },
  maintenance: { label: "Maintenance", variant: "warning" },
  promotion: { label: "Promotion", variant: "default" },
  info: { label: "Info", variant: "secondary" },
};

function truncateAlert(text: string, maxLength: number): string {
  return text.length <= maxLength ? text : text.slice(0, maxLength).trimEnd() + "...";
}

function NotificationsList({
  alerts,
  onMarkRead,
  formatDate,
}: {
  alerts: UserAlert[] | undefined;
  onMarkRead: (id: string) => void;
  formatDate: (date: string | number | Date) => string;
}) {
  const { t } = useTranslation("nav");
  if (!alerts || alerts.length === 0) {
    return <p className="py-6 text-center text-[13px] text-dim">{t("noNotificationsYet")}</p>;
  }
  return (
    <div className="-mx-2 flex flex-col">
      {alerts.map((alert) => {
        const badgeConfig = ALERT_TYPE_BADGE[alert.type] ?? {
          label: alert.type,
          variant: "secondary" as const,
        };
        return (
          <button
            key={alert.id}
            type="button"
            className="flex w-full gap-3 rounded-md p-2 text-left hover:bg-muted"
            onClick={() => {
              if (!alert.isRead) onMarkRead(alert.id);
            }}
          >
            <span className="mt-1.5 shrink-0">
              {!alert.isRead ? (
                <span className="block h-2 w-2 rounded-full bg-status-success" />
              ) : (
                <span className="block h-2 w-2" />
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-start justify-between gap-2">
                <span className="text-[13px] font-medium leading-tight">{alert.title}</span>
                <Badge variant={badgeConfig.variant} className="shrink-0">
                  {badgeConfig.label}
                </Badge>
              </span>
              <span className="mt-0.5 block text-xs text-dim">
                {truncateAlert(alert.body, 100)}
              </span>
              <span className="mt-1 block text-[11px] text-faint">
                {formatDate(alert.createdAt)}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
